import React, { useEffect, useState } from 'react';
import { MessageBus } from '@podium/browser';
import classNames from 'classnames';
import utils from '@gjensidige/core-utilities-css';

const messageBus = new MessageBus();
import styles from '../less/footer.module.less';

const CallMe = ({ apiUrl, baseUrl = '/', text, target }) => {
  if (!apiUrl || (!text && !target)) return null;
  const [callMeData, setCallMeData] = useState({} as CallMeDataProps);

  const submitRequest = (event) => {
    // @ts-ignore
    const { phoneNumber, queue, customerName, comment, insuranceNumber } =
      event.payload;
    if (phoneNumber && queue) {
      handleSubmit(phoneNumber, queue, customerName, comment, insuranceNumber);
    }
  };

  useEffect(() => {
    messageBus.subscribe('callMe', 'submit', submitRequest);
    return () => messageBus.unsubscribe('callMe', 'submit', submitRequest);
  }, [callMeData]);

  useEffect(() => {
    const getCallMeData = async () => {
      await fetch(`${baseUrl}${apiUrl}`, {
        headers: {
          Accept: 'application/json; charset=UTF-8',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `HTTP error! status: ${response.status} - ${response.statusText}`
            );
          }
          return response.json();
        })
        .then((data) => setCallMeData(data))
        .catch(() => {
          return;
        });
    };
    getCallMeData();
  }, []);

  async function getStatusAndJson(response) {
    try {
      const json = await response.json();
      return {
        httpStatus: response.status,
        json
      };
    } catch (e) {
      return { httpStatus: response.status, json: {} };
    }
  }

  const parseStatusCode = ({ httpStatus, json }) => {
    let callStatusCode = json.code;

    if (httpStatus === 200) {
      callStatusCode = callMeData.statusCodes.STATUS_OK;
    } else if (!json) {
      throw Error(`No JSON data in response. HTTP status code: ${httpStatus}`);
    }

    return { callStatusCode, httpStatus, json };
  };

  const handleStatusCode = ({ callStatusCode }) => {
    if (callStatusCode === callMeData.statusCodes.STATUS_OK) {
      messageBus.publish('callMe', 'response', {
        statusCode: callStatusCode
      });
    } else {
      const errorMessage =
        callMeData.statusCodes.ERRORMESSAGES_BY_STATUS[
          `code_${callStatusCode}`
        ];

      messageBus.publish('callMe', 'response', {
        statusCode: !errorMessage
          ? callMeData.statusCodes.STATUS_GENERIC
          : callStatusCode
      });
    }
  };

  const handleError = () => {
    messageBus.publish('callMe', 'response', {
      statusCode: callMeData.statusCodes.STATUS_GENERIC ?? '-1'
    });
  };

  const handleSubmit = (
    phoneNumber: string,
    queue: string,
    customerName: string,
    comment: string,
    insuranceNumber: string
  ) => {
    (async () => {
      const response = await fetch(`${baseUrl}${callMeData.callbackEndPoint}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json; charset=UTF-8',
          'Content-Type': 'application/json; charset=UTF-8',
          'X-ClientId': callMeData.xClientId,
          'X-ClientVersion': callMeData.xClientVersion
        },
        body: JSON.stringify({
          queue,
          phoneNumber,
          customerName: customerName ?? null,
          comment: comment ?? null,
          insuranceNumber: insuranceNumber ?? null
        }),
        mode: 'cors', // no-cors, cors, *same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'same-origin', // include, *same-origin, omit
        redirect: 'follow', // manual, *follow, error
        referrer: 'no-referrer' // no-referrer, *client
      });

      return response;
    })()
      .then(getStatusAndJson)
      .then(parseStatusCode)
      .then(handleStatusCode)
      .catch(handleError);
  };

  return Object.entries(callMeData)?.length ? (
    <div className={classNames(utils['mb-2'], utils['mb-md-3'])}>
      <a className={styles.callMeButton} href={target}>
        {text ?? 'Call me'}
      </a>
    </div>
  ) : null;
};

export default CallMe;

type CallMeDataProps = {
  xClientId?: string;
  xClientVersion?: string;
  xClientName?: string;
  callbackEndPoint?: string;
  statusEndPoint?: string;
  statusCodes?: {
    STATUS_OK: string;
    STATUS_CLOSED?: string;
    STATUS_GENERIC?: string;
    STATUS_INVALID_PHONE?: string;
    STATUS_REQUESTED?: string;
    STATUS_COMMUNICATION_FAILED?: string;
    STATUS_INVALID_INPUT?: string;
    ERRORMESSAGES_BY_STATUS?: {
      'code_-1'?: string;
      code_3001?: string;
      code_2010?: string;
      code_1200?: string;
      code_15?: string;
      code_4005?: string;
    };
  };
};
